function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useEffect, useMemo, useState, useRef } from 'react';
import cn from 'classnames';
import { Popper, ClickAwayListener } from '@material-ui/core';
import { PopoverArrowIcon } from "../PopoverArrowIcon";
import styles from "./PopoverOnHover.module.scss";
import zIndex from "../../foundations/zIndex.module.scss";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
PopoverOnHover.defaultProps = {
  placement: 'top',
  timeout: 200,
  openStrategy: 'auto',
  removePadding: false
};

/**
 * Opens a popover that stays open as long as you keep the mouse hovered on the content.
 */
export function PopoverOnHover(props) {
  const {
    trigger,
    content,
    timeout,
    id,
    isOpen,
    placement = 'top',
    onOpen,
    onClose,
    openStrategy,
    showArrow,
    removePadding,
    container
  } = props;
  const [anchorEl, setAnchorEl] = useState(null);
  const timeoutIdRef = useRef(undefined);
  const [open, setOpen] = useState(isOpen || false);
  useEffect(() => {
    return () => {
      clearTimeout(timeoutIdRef.current);
    };
  }, []);
  useEffect(() => {
    setOpen(isOpen || false);
  }, [isOpen]);
  const popperId = useMemo(() => {
    return id || Math.random().toString();
  }, [id]);
  const schedulePopoverHiding = () => {
    timeoutIdRef.current = setTimeout(() => {
      setOpen(false);
      onClose && onClose();
    }, timeout);
  };
  const triggerPropsCommon = {
    ref: el => setAnchorEl(el),
    ['aria-owns']: open ? popperId : undefined,
    ['aria-haspopup']: 'true',
    ['data-popover-is-open']: open ? 'true' : 'false'
  };
  const triggerPropsHoverStrategy = _objectSpread({
    onMouseEnter: () => {
      setOpen(true);
      clearTimeout(timeoutIdRef.current);
      onOpen && onOpen();
    },
    onMouseLeave: schedulePopoverHiding
  }, triggerPropsCommon);
  const triggerPropsClickStrategy = _objectSpread({
    onClick: () => {
      setOpen(!open);
      if (open) {
        onClose && onClose();
      } else {
        onOpen && onOpen();
      }
    }
  }, triggerPropsCommon);
  const targetContainerPropsHoverStrategy = {
    onClick: e => e.stopPropagation(),
    onMouseEnter: () => {
      clearTimeout(timeoutIdRef.current);
    },
    onMouseLeave: schedulePopoverHiding
  };
  const targetContainerPropsClickStrategy = {
    onClick: event => event.stopPropagation()
  };
  const targetContainerPropsAutoStrategy = {
    onClick: event => event.stopPropagation()
  };
  let triggerProps = {},
    targetContainerProps = {};
  switch (openStrategy) {
    case 'auto':
      if (isOpen === undefined) {
        // if no param is passed => change to hover strategy
        triggerProps = triggerPropsHoverStrategy;
        targetContainerProps = targetContainerPropsHoverStrategy;
      } else {
        triggerProps = triggerPropsCommon;
        targetContainerProps = targetContainerPropsAutoStrategy;
      }
      break;
    case 'hover':
      triggerProps = triggerPropsHoverStrategy;
      targetContainerProps = targetContainerPropsHoverStrategy;
      break;
    case 'click':
      triggerProps = triggerPropsClickStrategy;
      targetContainerProps = targetContainerPropsClickStrategy;
      break;
  }
  const contentProps = {
    closePopover: () => setOpen(false)
  };
  const placementClass = useMemo(() => {
    if (placement.startsWith('top')) {
      return 'top';
    } else if (placement.startsWith('bottom')) {
      return 'bottom';
    } else if (placement.startsWith('left')) {
      return 'left';
    } else {
      return 'right';
    }
  }, [placement]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [trigger(triggerProps), /*#__PURE__*/_jsx("div", _objectSpread(_objectSpread({}, targetContainerProps), {}, {
      children: anchorEl && /*#__PURE__*/_jsxs(Popper, {
        style: {
          zIndex: zIndex.popover
        },
        id: popperId,
        open: open,
        anchorEl: anchorEl,
        placement: placement,
        className: styles[placementClass],
        container: container,
        modifiers: {
          offset: {
            // Distance from the trigger element.
            // See: https://popper.js.org/docs/v1/#modifiers..offset
            offset: '0, 8'
          }
        },
        children: [showArrow && /*#__PURE__*/_jsxs("span", {
          className: cn(styles.arrow),
          "x-arrow": "true",
          children: [/*#__PURE__*/_jsx("span", {
            className: styles.arrowShadow
          }), /*#__PURE__*/_jsx(PopoverArrowIcon, {
            className: styles.arrowCover
          })]
        }), /*#__PURE__*/_jsx(ClickAwayListener, {
          onClickAway: () => openStrategy !== 'auto' && setOpen(false),
          children: /*#__PURE__*/_jsx("div", {
            className: cn(styles.popoverContent, {
              [styles.withArrow]: showArrow,
              [styles.builtInPadding]: !removePadding
            }),
            children: typeof content === 'function' ? content(contentProps) : content
          })
        })]
      })
    }))]
  });
}